import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { BarChart, Bar, CartesianGrid, Cell, Legend, PieChart, Pie, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { CheckCircle2, CircleAlert, Copy, Download, ExternalLink, FileWarning, RefreshCw, Share2, WalletCards } from "lucide-react";
import "@/App.css";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const money = (v) => new Intl.NumberFormat("id-ID", { style: "currency", currency: "IDR", maximumFractionDigits: 0 }).format(v);
const moneyCompact = (v) => `Rp ${new Intl.NumberFormat("id-ID", { notation: "compact", maximumFractionDigits: 2 }).format(v)}`;
const numberFmt = (v) => new Intl.NumberFormat("id-ID").format(v);
const PALETTE = ["#10B981", "#C4302B", "#F59E0B", "#0EA5E9", "#8B5CF6", "#F97316"];

export default function PublicSummary() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState("");
  const [copied, setCopied] = useState(false);

  const load = async (refresh = false) => {
    try {
      if (refresh) setRefreshing(true);
      const res = await axios.get(`${API}/public-summary`, { params: refresh ? { refresh: true } : {} });
      setData(res.data);
      setError("");
    } catch (_) {
      setError("Ringkasan belum dapat dimuat. Coba beberapa saat lagi.");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => { load(); }, []);

  const share = async () => {
    try {
      const url = window.location.origin + "/ringkas";
      if (navigator.share) {
        await navigator.share({ title: "Ringkasan KIPK LLDIKTI VII", url });
      } else {
        await navigator.clipboard.writeText(url);
        setCopied(true);
        setTimeout(() => setCopied(false), 2200);
      }
    } catch (_) { /* user cancelled */ }
  };

  const kerugianYears = useMemo(() => (data?.kerugian?.per_year || []).map((y) => ({ year: String(y.year), kerugian: y.kerugian / 1e9, dikembalikan: y.dikembalikan / 1e9, belum: y.belum / 1e9 })), [data]);
  const masalahYears = useMemo(() => (data?.masalah?.per_year || []).map((y) => ({ year: String(y.year), selesai: y.selesai, lain: y.lain })), [data]);
  const ptStatusKerugian = useMemo(() => data ? [
    { name: "PT Selesai", value: data.kerugian.pt_selesai },
    { name: "PT Dalam Proses", value: data.kerugian.pt_proses },
  ] : [], [data]);
  const ptStatusMasalah = useMemo(() => data ? [
    { name: "PT Selesai", value: data.masalah.pt_selesai },
    { name: "PT Dalam Proses", value: data.masalah.pt_proses },
  ] : [], [data]);
  const bucketData = useMemo(() => {
    if (!data) return [];
    const b = data.kerugian.bucket_belum;
    return [
      { label: "< Rp 100 jt", value: b["<100jt"] || 0 },
      { label: "Rp 100 – 500 jt", value: b["100-500jt"] || 0 },
      { label: "Rp 500 jt – 1 M", value: b["500jt-1M"] || 0 },
      { label: "≥ Rp 1 M", value: b[">=1M"] || 0 },
    ];
  }, [data]);

  if (loading) return <div className="loading-screen" data-testid="public-loading">Memuat ringkasan publik…</div>;
  if (error) return <div className="loading-screen"><CircleAlert size={22} /> {error}</div>;

  const k = data.kerugian;
  const m = data.masalah;
  const kYears = k.years;
  const kPeriod = kYears.length ? `${kYears[0]}–${kYears[kYears.length - 1]}` : "—";

  return (
    <div className="public-shell">
      <header className="public-header">
        <div className="public-brand">
          <img src="/logo-kemdikbud.png" alt="Logo Kemendikbud" className="public-brand-logo" />
          <div>
            <div className="eyebrow" data-testid="public-eyebrow">RINGKASAN PUBLIK · TANPA NAMA PT</div>
            <h1 data-testid="public-title">Pusat Kendali Masalah KIPK</h1>
            <p className="public-subtitle">LLDIKTI Wilayah VII · Periode {kPeriod}</p>
          </div>
        </div>
        <div className="public-actions">
          <button className="download-button ghost" data-testid="public-share-button" onClick={share} aria-label="Bagikan ringkasan">
            <Share2 size={15} /> <span>{copied ? "Tautan disalin" : "Bagikan"}</span>
          </button>
          <a className="download-button" href={`${API}/report/pdf?source=kerugian&anonymize=true`} target="_blank" rel="noopener noreferrer" data-testid="public-download-kerugian">
            <Download size={15} /> <span>PDF Kerugian</span>
          </a>
          <a className="download-button" href={`${API}/report/pdf?source=masalah&anonymize=true`} target="_blank" rel="noopener noreferrer" data-testid="public-download-masalah">
            <Download size={15} /> <span>PDF Non-Kerugian</span>
          </a>
          <button className="icon-button" data-testid="public-refresh" onClick={() => load(true)} disabled={refreshing} aria-label="Segarkan"><RefreshCw size={15} className={refreshing ? "spin" : ""} /></button>
        </div>
      </header>

      <div className="public-note" data-testid="public-note">
        <CircleAlert size={14} /> Ringkasan ini menyajikan agregat 3 tahun terakhir tanpa mencantumkan nama perguruan tinggi. Untuk detail per PT, buka <Link to="/" className="inline-link">dashboard lengkap</Link>.
      </div>

      <section className="public-section" data-testid="public-kerugian-section">
        <h2><WalletCards size={18} /> Temuan Kerugian ({kPeriod})</h2>
        <div className="public-kpi-grid">
          <PublicKpi label="Total kasus" value={numberFmt(k.total_kasus)} helper={`${k.total_pt} perguruan tinggi terlibat`} testid="pk-kerugian-kasus" />
          <PublicKpi label="Nilai temuan" value={moneyCompact(k.total_kerugian)} helper={money(k.total_kerugian)} testid="pk-kerugian-nilai" />
          <PublicKpi label="Sudah dikembalikan" value={moneyCompact(k.total_dikembalikan)} helper={`${k.total_kerugian ? Math.round(k.total_dikembalikan / k.total_kerugian * 1000) / 10 : 0}% dari nilai temuan`} tone="good" testid="pk-kerugian-dikembalikan" />
          <PublicKpi label="Belum dikembalikan" value={moneyCompact(k.total_belum)} helper={`${k.total_kerugian ? Math.round(k.total_belum / k.total_kerugian * 1000) / 10 : 0}% masih tertahan`} tone="bad" testid="pk-kerugian-belum" />
          <PublicKpi label="PT sudah selesai" value={numberFmt(k.pt_selesai)} helper={`${k.pt_selesai + k.pt_proses ? Math.round(k.pt_selesai / (k.pt_selesai + k.pt_proses) * 100) : 0}% dari PT terlibat`} tone="good" testid="pk-kerugian-pt-selesai" />
          <PublicKpi label="PT masih dalam proses" value={numberFmt(k.pt_proses)} helper="Tindak lanjut belum tuntas" tone="bad" testid="pk-kerugian-pt-proses" />
        </div>

        <div className="public-charts">
          <div className="public-chart-card">
            <div className="eyebrow">Nilai temuan &amp; pengembalian per tahun (miliar Rp)</div>
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={kerugianYears} margin={{ top: 12, right: 12, left: -8, bottom: 4 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
                <XAxis dataKey="year" tick={{ fill: "#59616A", fontSize: 12 }} />
                <YAxis tick={{ fill: "#59616A", fontSize: 12 }} />
                <Tooltip formatter={(v, k2) => [`Rp ${v.toFixed(2)} M`, k2 === "kerugian" ? "Nilai temuan" : k2 === "dikembalikan" ? "Sudah dikembalikan" : "Belum dikembalikan"]} />
                <Legend formatter={(name) => name === "kerugian" ? "Nilai temuan" : name === "dikembalikan" ? "Sudah dikembalikan" : "Belum dikembalikan"} />
                <Bar dataKey="kerugian" fill="#F59E0B" />
                <Bar dataKey="dikembalikan" fill="#10B981" />
                <Bar dataKey="belum" fill="#C4302B" />
              </BarChart>
            </ResponsiveContainer>
          </div>
          <div className="public-chart-card">
            <div className="eyebrow">Status penyelesaian PT (kumulatif 3 tahun)</div>
            <ResponsiveContainer width="100%" height={280}>
              <PieChart>
                <Tooltip formatter={(v) => `${v} PT`} />
                <Legend />
                <Pie data={ptStatusKerugian} dataKey="value" nameKey="name" innerRadius={60} outerRadius={100} paddingAngle={2}>
                  {ptStatusKerugian.map((_, i) => <Cell key={i} fill={PALETTE[i]} />)}
                </Pie>
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="public-chart-card wide">
            <div className="eyebrow">Distribusi kasus tunggakan berdasarkan besar nominal</div>
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={bucketData} margin={{ top: 12, right: 12, left: -8, bottom: 4 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
                <XAxis dataKey="label" tick={{ fill: "#59616A", fontSize: 11 }} />
                <YAxis allowDecimals={false} tick={{ fill: "#59616A", fontSize: 12 }} />
                <Tooltip formatter={(v) => [`${v} kasus`, "Jumlah"]} />
                <Bar dataKey="value" fill="#0EA5E9" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="public-year-grid">
          {k.per_year.map((y) => (
            <div key={y.year} className="public-year-card">
              <div className="eyebrow">TAHUN {y.year}</div>
              <ul>
                <li><span>Kasus</span><strong>{numberFmt(y.kasus)}</strong></li>
                <li><span>PT terlibat</span><strong>{numberFmt(y.pt)}</strong></li>
                <li><span>Nilai temuan</span><strong>{moneyCompact(y.kerugian)}</strong></li>
                <li><span>Sudah dikembalikan</span><strong className="good">{moneyCompact(y.dikembalikan)}</strong></li>
                <li><span>Belum dikembalikan</span><strong className={y.belum ? "bad" : ""}>{moneyCompact(y.belum)}</strong></li>
                <li><span>PT selesai / dalam proses</span><strong>{y.pt_selesai} / {y.pt_proses}</strong></li>
                <li><span>Progres pengembalian</span><strong>{y.persen}%</strong></li>
              </ul>
            </div>
          ))}
        </div>
      </section>

      <section className="public-section" data-testid="public-masalah-section">
        <h2><FileWarning size={18} /> Masalah Non-Kerugian ({m.years.length ? `${m.years[0]}–${m.years[m.years.length - 1]}` : "—"})</h2>
        <div className="public-kpi-grid">
          <PublicKpi label="Total kasus" value={numberFmt(m.total_kasus)} helper={`${m.total_pt} perguruan tinggi terlibat`} testid="pk-masalah-kasus" />
          <PublicKpi label="Kasus selesai" value={numberFmt(m.kasus_selesai)} helper={`${m.total_kasus ? Math.round(m.kasus_selesai / m.total_kasus * 100) : 0}% dari total`} tone="good" testid="pk-masalah-selesai" />
          <PublicKpi label="Butuh tindak lanjut" value={numberFmt(m.kasus_lain)} helper="Status belum selesai" tone="bad" testid="pk-masalah-lain" />
          <PublicKpi label="PT sudah selesai" value={numberFmt(m.pt_selesai)} helper={`${m.pt_selesai + m.pt_proses ? Math.round(m.pt_selesai / (m.pt_selesai + m.pt_proses) * 100) : 0}% dari PT terlibat`} tone="good" testid="pk-masalah-pt-selesai" />
          <PublicKpi label="PT masih dalam proses" value={numberFmt(m.pt_proses)} helper="Belum tuntas" tone="bad" testid="pk-masalah-pt-proses" />
        </div>
        <div className="public-charts">
          <div className="public-chart-card">
            <div className="eyebrow">Kasus non-kerugian per tahun</div>
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={masalahYears} margin={{ top: 12, right: 12, left: -8, bottom: 4 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
                <XAxis dataKey="year" tick={{ fill: "#59616A", fontSize: 12 }} />
                <YAxis allowDecimals={false} tick={{ fill: "#59616A", fontSize: 12 }} />
                <Tooltip />
                <Legend formatter={(name) => name === "selesai" ? "Selesai" : "Butuh tindak lanjut"} />
                <Bar dataKey="selesai" stackId="s" fill="#10B981" />
                <Bar dataKey="lain" stackId="s" fill="#C4302B" />
              </BarChart>
            </ResponsiveContainer>
          </div>
          <div className="public-chart-card">
            <div className="eyebrow">Status penyelesaian PT (kumulatif)</div>
            <ResponsiveContainer width="100%" height={280}>
              <PieChart>
                <Tooltip formatter={(v) => `${v} PT`} />
                <Legend />
                <Pie data={ptStatusMasalah} dataKey="value" nameKey="name" innerRadius={60} outerRadius={100} paddingAngle={2}>
                  {ptStatusMasalah.map((_, i) => <Cell key={i} fill={PALETTE[i]} />)}
                </Pie>
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
        <div className="public-year-grid">
          {m.per_year.map((y) => (
            <div key={y.year} className="public-year-card">
              <div className="eyebrow">TAHUN {y.year}</div>
              <ul>
                <li><span>Kasus</span><strong>{numberFmt(y.kasus)}</strong></li>
                <li><span>PT terlibat</span><strong>{numberFmt(y.pt)}</strong></li>
                <li><span>Selesai / Butuh tindak lanjut</span><strong>{y.selesai} / {y.lain}</strong></li>
                <li><span>PT selesai / dalam proses</span><strong>{y.pt_selesai} / {y.pt_proses}</strong></li>
              </ul>
            </div>
          ))}
        </div>
      </section>

      <footer className="public-footer">
        <div>
          <strong>Pusat Kendali Masalah KIPK LLDIKTI Wilayah VII</strong>
          <span>Sumber: Google Sheet resmi · Data digenerate pada {new Date(data.generated_at).toLocaleString("id-ID", { dateStyle: "medium", timeStyle: "short", timeZone: "Asia/Jakarta" })} WIB</span>
        </div>
        <Link className="public-cta-link" to="/" data-testid="open-full-dashboard">
          Buka dashboard lengkap <ExternalLink size={13} />
        </Link>
      </footer>
    </div>
  );
}

function PublicKpi({ label, value, helper, tone, testid }) {
  return (
    <div className={`public-kpi ${tone || ""}`} data-testid={testid}>
      <span>{label}</span>
      <strong data-testid={`${testid}-value`}>{value}</strong>
      <small>{helper}</small>
    </div>
  );
}
